import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FileBadge2, AlertTriangle, CheckCircle2, Download, Loader2 } from "lucide-react";
import { api, BRL } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import BoletoBox from "@/components/BoletoBox";

const Opt = ({ active, disabled, onClick, children, testid }) => (
  <button type="button" disabled={disabled} onClick={onClick} data-testid={testid}
    className={`rounded-lg border-2 px-2 py-1.5 text-[11px] font-bold transition-colors disabled:opacity-50 ${active ? "border-[#1e3a8a] bg-blue-50 text-[#1e3a8a]" : "border-slate-200 text-slate-500"}`}>
    {children}
  </button>
);

function TermPicker({ terms, value, onChange }) {
  return (
    <div className="space-y-1.5" data-testid="nfse-term-picker">
      <p className="text-[11px] font-black uppercase text-slate-600">Informações complementares (termo)</p>
      <div className="grid grid-cols-3 gap-1.5">
        <Opt active={value === "colaboracao"} disabled={!terms.colaboracao} onClick={() => onChange("colaboracao")} testid="nfse-term-colaboracao">Colaboração</Opt>
        <Opt active={value === "pdde"} onClick={() => onChange("pdde")} testid="nfse-term-pdde">PDDE</Opt>
        <Opt active={value === "nenhum"} onClick={() => onChange("nenhum")} testid="nfse-term-nenhum">Nenhum</Opt>
      </div>
      {terms[value] && <p className="text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded p-2 break-words" data-testid="nfse-term-text">{terms[value]}</p>}
      {!terms.colaboracao && <p className="text-[10.5px] text-slate-400">Escola sem número do termo de colaboração cadastrado.</p>}
    </div>
  );
}

function PayPicker({ value, onChange, due, setDue, calc, missing }) {
  return (
    <div className="space-y-1.5" data-testid="nfse-pay-picker">
      <p className="text-[11px] font-black uppercase text-slate-600">Forma de pagamento</p>
      <div className="grid grid-cols-2 gap-1.5">
        <Opt active={value === "transferencia"} onClick={() => onChange("transferencia")} testid="nfse-pay-transferencia">Transferência</Opt>
        <Opt active={value === "boleto"} onClick={() => onChange("boleto")} testid="nfse-pay-boleto">Boleto</Opt>
      </div>
      {value === "boleto" && (
        <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-2.5 space-y-2" data-testid="nfse-boleto-box">
          <p className="text-[11px] text-slate-700 tabular-nums" data-testid="nfse-boleto-calc">{BRL(calc.total)} − ISS retido {BRL(calc.iss)} = <b>Boleto de {BRL(calc.valor)}</b></p>
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold text-slate-600 shrink-0">Vencimento</span>
            <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="h-8 bg-white" data-testid="nfse-boleto-due" />
          </div>
          {missing.length > 0 && <p className="text-[11px] text-amber-800" data-testid="nfse-boleto-missing">Falta para o boleto: {missing.join(", ")}</p>}
          <p className="text-[10.5px] text-slate-500">Sem juros e sem multa. O boleto (com Pix) vai junto no pacote da papelada.</p>
        </div>
      )}
    </div>
  );
}

const REASONS = [[1, "Erro na emissão"], [2, "Serviço não prestado"], [9, "Outros"]];

function CancelBox({ budgetId, onDone }) {
  const [open, setOpen] = useState(false);
  const [cod, setCod] = useState(1);
  const [txt, setTxt] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!window.confirm("Cancelar esta NFS-e na Receita? Não tem como desfazer.")) return;
    setBusy(true);
    try {
      await api.post(`/budgets/${budgetId}/nfse/cancel`, { c_motivo: cod, x_motivo: txt.trim() });
      toast.success("NFS-e cancelada");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao cancelar", { duration: 9000 });
    } finally { setBusy(false); }
  };
  if (!open) {
    return <button type="button" onClick={() => setOpen(true)} className="text-xs font-semibold text-rose-600 underline" data-testid="nfse-cancel-open">Cancelar esta nota</button>;
  }
  return (
    <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-3 space-y-2" data-testid="nfse-cancel-box">
      <p className="text-xs font-bold text-rose-800">Cancelar NFS-e</p>
      <div className="grid grid-cols-3 gap-1">
        {REASONS.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setCod(k)} data-testid={`nfse-cancel-reason-${k}`}
            className={`rounded border-2 py-1 text-[10.5px] font-bold ${cod === k ? "border-rose-500 bg-white text-rose-700" : "border-slate-200 text-slate-500"}`}>{l}</button>
        ))}
      </div>
      <Textarea value={txt} onChange={(e) => setTxt(e.target.value)} rows={2} placeholder="Explique o motivo (mínimo 15 letras)" className="bg-white" data-testid="nfse-cancel-text" />
      <p className="text-[10.5px] text-slate-500">O prazo é conferido pela Receita. O orçamento volta para "Finalizado"; se já lançou a apuração financeira, revise-a.</p>
      <div className="flex gap-2 justify-end">
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Voltar</Button>
        <Button size="sm" onClick={send} disabled={busy || txt.trim().length < 15} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="nfse-cancel-confirm">{busy ? "Cancelando..." : "Cancelar nota"}</Button>
      </div>
    </div>
  );
}

export default function NfseDialog({ open, onOpenChange, budgetId, onEditSchool, onEmitted }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showXml, setShowXml] = useState(false);
  const [pay, setPay] = useState("transferencia");
  const [term, setTerm] = useState("nenhum");
  const [due, setDue] = useState("");
  const load = () => api.get(`/budgets/${budgetId}/nfse`).then((r) => { setD(r.data); setTerm(r.data.default_term || "nenhum"); }).catch((e) => toast.error(e?.response?.data?.detail || "Erro ao carregar"));
  useEffect(() => { if (open) { setD(null); load(); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const emit = async () => {
    const prod = d.ambiente === "producao";
    const extra = pay === "boleto" ? `\n\nE gerar boleto de ${BRL(d.boleto_calc.valor)} com vencimento ${due.split("-").reverse().join("/")}.` : "";
    if (!window.confirm((prod ? `Emitir NFS-e REAL de ${BRL(d.total)}? Ela vale como nota fiscal.` : "Emitir nota de TESTE (sem valor fiscal)?") + extra)) return;
    setBusy(true);
    try {
      const r = await api.post(`/budgets/${budgetId}/nfse/emit`, { payment_method: pay, term, due_date: pay === "boleto" ? due : "" });
      toast.success(`NFS-e Nº ${r.data.numero || ""} emitida${r.data.pdf_saved ? " — PDF já anexado à papelada" : ""}${r.data.boleto ? " + boleto gerado" : ""}`);
      if (r.data.boleto_error) toast.error(`Nota emitida, mas o boleto falhou: ${r.data.boleto_error}. Gere de novo na tela da nota.`, { duration: 12000 });
      onEmitted?.(r.data); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao emitir", { duration: 9000 });
    } finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/budgets/${budgetId}/nfse/danfse`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `NFSe_${d.nfse?.numero || ""}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF da nota ainda não disponível"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden w-[calc(100vw-1.5rem)] sm:w-full grid-cols-1 [&>*]:min-w-0" data-testid="nfse-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileBadge2 className="w-5 h-5 text-[#1e3a8a]" /> Nota fiscal (NFS-e)</DialogTitle>
          <DialogDescription className="text-xs">{d ? (d.ambiente === "producao" ? "Ambiente de PRODUÇÃO — nota real" : "Ambiente de TESTES — sem valor fiscal") : "Carregando..."}</DialogDescription>
        </DialogHeader>
        {!d ? <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /> : d.nfse?.chave && !d.nfse?.cancelada ? (
          <div className="space-y-3" data-testid="nfse-emitted">
            <p className="text-sm text-emerald-700 font-bold flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> NFS-e Nº {d.nfse.numero} emitida</p>
            <p className="text-[11px] text-slate-500 break-all">Chave: {d.nfse.chave}</p>
            {d.nfse.payment_method && <p className="text-[11px] text-slate-600" data-testid="nfse-payment-info">Pagamento: <b>{d.nfse.payment_method === "boleto" ? "Boleto" : "Transferência"}</b></p>}
            <Button onClick={downloadPdf} className="w-full" data-testid="nfse-download-pdf"><Download className="w-4 h-4 mr-1.5" /> Baixar PDF da nota</Button>
            {(d.nfse.payment_method === "boleto" || d.boleto?.numero) && <BoletoBox budgetId={budgetId} boleto={d.boleto} onChanged={() => { onEmitted?.(); load(); }} />}
            <CancelBox budgetId={budgetId} onDone={() => { onEmitted?.(); load(); }} />
          </div>
        ) : (
          <div className="space-y-3">
            {d.nfse?.cancelada && <p className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2" data-testid="nfse-cancelled-info">NFS-e Nº {d.nfse.numero} cancelada — {d.nfse.motivo}</p>}
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
              <p className="text-xs text-slate-500">Valor da nota</p>
              <p className="text-lg font-black tabular-nums" data-testid="nfse-total">{BRL(d.total)}</p>
              <p className="text-[11px] text-slate-600 whitespace-pre-wrap break-words mt-2 max-h-40 overflow-y-auto" data-testid="nfse-description">{d.description}</p>
              <p className={`text-[11px] mt-1 font-bold tabular-nums ${d.description.length > d.desc_limit ? "text-rose-600" : "text-slate-500"}`} data-testid="nfse-desc-count">
                {d.description.length} / {d.desc_limit} letras{d.description.length > d.desc_limit ? ` — passou ${d.description.length - d.desc_limit}. Encurte os itens do orçamento.` : ""}
              </p>
            </div>
            <TermPicker terms={d.terms} value={term} onChange={setTerm} />
            <PayPicker value={pay} onChange={setPay} due={due} setDue={setDue} calc={d.boleto_calc} missing={d.boleto_missing} />
            {d.missing.length > 0 && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 space-y-1" data-testid="nfse-missing">
                <p className="font-bold flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Falta preencher:</p>
                <ul className="list-disc pl-5">{d.missing.map((m) => <li key={m}>{m}</li>)}</ul>
                <div className="flex flex-wrap gap-3 pt-1">
                  <button type="button" onClick={onEditSchool} className="font-semibold underline" data-testid="nfse-edit-school">Editar dados da escola</button>
                  <Link to="/minha-conta" className="font-semibold underline" data-testid="nfse-open-settings">Dados fiscais / certificado</Link>
                </div>
              </div>
            )}
            {!d.has_certificate && <p className="text-xs text-slate-500" data-testid="nfse-no-cert">Aguardando o certificado digital A1 — cadastre em Minha Conta.</p>}
            <Button onClick={emit} disabled={busy || d.missing.length > 0 || !d.has_certificate || (term === "colaboracao" && !d.terms.colaboracao) || (pay === "boleto" && (!due || d.boleto_missing.length > 0))} className="w-full bg-[#1e3a8a] hover:bg-[#1e40af] text-white" data-testid="nfse-emit-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileBadge2 className="w-4 h-4 mr-1.5" />} Emitir NFS-e
            </Button>
            <button type="button" onClick={() => setShowXml((v) => !v)} className="text-[11px] text-slate-500 underline" data-testid="nfse-toggle-xml">{showXml ? "Esconder" : "Ver"} dados técnicos (XML)</button>
            {showXml && <pre className="text-[10px] bg-slate-900 text-slate-100 rounded p-2 overflow-x-auto max-h-60" data-testid="nfse-xml">{d.xml_preview}</pre>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
